import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TVMInputs, CalculationType } from './types';

interface TVMInputFormProps {
  inputs: TVMInputs;
  calculationType: CalculationType;
  onInputChange: (field: keyof TVMInputs, value: string) => void;
  onCalculationTypeChange: (type: CalculationType) => void;
  onCalculate: () => void;
}

const TVMInputForm = ({ 
  inputs, 
  calculationType, 
  onInputChange, 
  onCalculationTypeChange, 
  onCalculate 
}: TVMInputFormProps) => {
  const handleBalloonToggle = (checked: boolean) => {
    if (!checked) {
      onInputChange('paymentsUntilBalloon', '0');
      onInputChange('balloonPayment', '0');
    } else {
      // Set a default value when enabling balloon payment
      onInputChange('paymentsUntilBalloon', '60');
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const showBalloonSection = (inputs.paymentsUntilBalloon || 0) > 0;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-lg font-semibold">What do you want to calculate?</Label>
        <select 
          className="w-full p-3 border rounded-md text-sm"
          value={calculationType}
          onChange={(e) => onCalculationTypeChange(e.target.value as CalculationType)}
        >
          <option value="payment">Calculate Monthly Payment</option>
          <option value="balance">Calculate Current Balance</option>
          <option value="periods">Calculate Time to Pay Off</option>
        </select>
      </div>
      
      <div className="space-y-2">
        <Label>Original Loan Amount</Label>
        <Input
          type="number"
          step="0.01"
          value={inputs.presentValue || ''}
          onChange={(e) => onInputChange('presentValue', e.target.value)}
          placeholder="Enter original loan amount"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Annual Interest Rate (%)</Label>
        <Input
          type="number"
          step="0.01"
          value={inputs.interestRate || ''}
          onChange={(e) => onInputChange('interestRate', e.target.value)}
          placeholder="e.g., 8.5"
        />
      </div>
      
      {calculationType === 'payment' && (
        <div className="space-y-2">
          <Label>Loan Term (Months)</Label>
          <Input
            type="number"
            value={inputs.periods || ''}
            onChange={(e) => onInputChange('periods', e.target.value)}
            placeholder="e.g., 360 (for 30 years)"
          />
        </div>
      )}
      
      {calculationType === 'balance' && (
        <>
          <div className="space-y-2">
            <Label>Monthly Payment</Label>
            <Input
              type="number"
              step="0.01"
              value={inputs.payment || ''}
              onChange={(e) => onInputChange('payment', e.target.value)}
              placeholder="Enter monthly payment"
            />
          </div>
          <div className="space-y-2">
            <Label>Number of Payments Made (Months)</Label>
            <Input
              type="number"
              value={inputs.periods || ''}
              onChange={(e) => onInputChange('periods', e.target.value)}
              placeholder="e.g., 28"
            />
          </div>
        </>
      )}
      
      {calculationType === 'periods' && (
        <div className="space-y-2">
          <Label>Monthly Payment</Label>
          <Input
            type="number"
            step="0.01"
            value={inputs.payment || ''}
            onChange={(e) => onInputChange('payment', e.target.value)}
            placeholder="Enter monthly payment"
          />
        </div>
      )}
      
      <div className="space-y-2">
        <Label>Payments Per Year</Label>
        <select 
          className="w-full p-2 border rounded-md"
          value={inputs.paymentsPerYear || 12}
          onChange={(e) => onInputChange('paymentsPerYear', e.target.value)}
        >
          <option value="12">Monthly (12)</option>
          <option value="6">Bi-monthly (6)</option>
          <option value="4">Quarterly (4)</option>
          <option value="2">Semi-annually (2)</option>
          <option value="1">Annually (1)</option>
        </select>
      </div>
      
      {/* Balloon Payment Section */}
      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
        <div className="flex items-center space-x-2 mb-3">
          <Checkbox
            id="hasBalloon"
            checked={showBalloonSection}
            onCheckedChange={(checked) => handleBalloonToggle(checked === true)}
          />
          <Label htmlFor="hasBalloon" className="font-medium text-orange-800 cursor-pointer">
            Calculate balloon payment
          </Label>
        </div>
        
        {showBalloonSection && (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-sm text-orange-700">
                Payments until balloon is due (months)
              </Label>
              <Input
                type="number"
                value={inputs.paymentsUntilBalloon || ''}
                onChange={(e) => onInputChange('paymentsUntilBalloon', e.target.value)}
                placeholder="e.g., 60"
                className="text-sm bg-white"
              />
              <p className="text-xs text-orange-600">
                This will calculate what the balloon payment amount will be after the specified number of payments
              </p>
            </div>
          </div>
        )}
      </div>
      
      <Button onClick={onCalculate} className="w-full bg-blue-600 hover:bg-blue-700">
        Calculate
      </Button>
    </div>
  );
};

export default TVMInputForm;
